/**
 * PublishedMedia — public rendering of authenticated Media Library images.
 * Only status=="published" derivatives ever reach these components; the
 * backend enforces it, so a missing image simply renders nothing.
 */
import React, { useEffect, useState } from "react";
import { Info } from "lucide-react";
import { API, recordMedia } from "../../lib/api";
import { EntityImageFallback } from "../EntityImageFallback";

const BASE = API.replace(/\/api$/, "");
const full = (u) => (u && u.startsWith("/") ? `${BASE}${u}` : u);

const cache = new Map();

export const useRecordMedia = (targetType, targetId) => {
  const key = `${targetType}:${targetId}`;
  const [images, setImages] = useState(() => cache.get(key) ?? null);
  useEffect(() => {
    if (!targetId) return undefined;
    if (cache.has(key)) { setImages(cache.get(key)); return undefined; }
    let dead = false;
    recordMedia(targetType, targetId)
      .then((imgs) => { cache.set(key, imgs); if (!dead) setImages(imgs); })
      .catch(() => { cache.set(key, []); if (!dead) setImages([]); });
    return () => { dead = true; };
  }, [key, targetType, targetId]);
  return images; // null = loading · [] = none
};

/* Contained, aspect-ratio-safe figure with responsive derivatives,
   lazy loading, reserved dimensions and an expandable credit line. */
export const ArchiveImage = ({ img, className = "", imgClassName = "" }) => {
  const [credit, setCredit] = useState(false);
  const ratio = img.width && img.height ? `${img.width} / ${img.height}` : "4 / 3";
  const attribution = [img.credit, img.source, img.rights].filter(Boolean).join(" · ");
  return (
    <figure className={`min-w-0 ${className}`} data-testid={`archive-image-${img.id}`}>
      <div className="relative w-full bg-black border border-[#2D2A26] overflow-hidden"
           style={{ aspectRatio: ratio, maxHeight: "60vh" }}>
        <picture>
          {img.urls.avif && <source srcSet={full(img.urls.avif)} type="image/avif" />}
          {img.urls.webp && <source srcSet={full(img.urls.webp)} type="image/webp" />}
          <img src={full(img.urls.jpeg || img.urls.webp)} alt={img.alt}
               loading="lazy" decoding="async"
               width={img.width || undefined} height={img.height || undefined}
               className={`absolute inset-0 w-full h-full object-contain ${imgClassName}`}
               style={{ filter: "sepia(0.12)" }} />
        </picture>
      </div>
      {(img.caption || attribution) && (
        <figcaption className="mt-1.5 flex items-start gap-2">
          <span className="text-[11px] text-[#A39E93] leading-snug min-w-0">
            {img.caption}
            {credit && attribution && (
              <span className="block font-mono text-[9px] text-[#757065] mt-0.5"
                    data-testid={`archive-image-credit-${img.id}`}>{attribution}</span>
            )}
          </span>
          {attribution && (
            <button type="button" onClick={() => setCredit((v) => !v)}
                    data-testid={`archive-image-credit-toggle-${img.id}`}
                    title="Attribution" aria-label="Toggle attribution"
                    className="shrink-0 text-[#5A554C] hover:text-[#A68F5E] transition-colors mt-0.5">
              <Info size={11} />
            </button>
          )}
        </figcaption>
      )}
    </figure>
  );
};

/* Section for record pages: lead image + supporting grid.
   Renders nothing when the record has no published images. */
export const RecordMediaGallery = ({ targetType = "any", targetId, heading = "AUTHENTICATED PHOTOGRAPHS", skipRoles = [] }) => {
  const images = useRecordMedia(targetType, targetId);
  if (!images || images.length === 0) return null;
  const visible = images.filter((i) => i.role !== "background_reference" && !skipRoles.includes(i.role));
  if (visible.length === 0) return null;
  const [lead, ...rest] = visible;
  return (
    <section className="mt-10" data-testid={`record-media-gallery-${targetId}`}>
      <div className="font-mono text-[9px] uppercase tracking-[0.4em] text-[#A68F5E] mb-3 flex items-center gap-2">
        <span className="w-6 h-px bg-[#A68F5E]/50" /> {heading}
      </div>
      <ArchiveImage img={lead} className="max-w-2xl" />
      {rest.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 max-w-2xl">
          {rest.slice(0, 8).map((img) => <ArchiveImage key={img.id} img={img} />)}
        </div>
      )}
    </section>
  );
};

/* Archival hero portrait for entity reference pages. Renders nothing when
   the entity has no published portrait — layout never reserves space. */
export const RecordPortrait = ({ targetId, targetType = "any", name = "", kind = "" }) => {
  const [credit, setCredit] = useState(false);
  const images = useRecordMedia(targetType, targetId);
  const img = images && images.find(
    (i) => !["internal_only", "background_reference"].includes(i.role));
  if (!img) {
    // Loaded-and-empty → the deliberate Archive placeholder (never while loading).
    if (Array.isArray(images) && kind) {
      return (
        <figure className="relative shrink-0 w-[132px] sm:w-[168px] md:w-[200px]"
                data-testid={`record-portrait-placeholder-${targetId}`}>
          <div className="relative border border-[#3A3630] bg-black p-1.5"
               style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.55)" }}>
            <EntityImageFallback kind={kind} className="w-full h-[150px] sm:h-[180px]" iconSize={22} />
          </div>
        </figure>
      );
    }
    return null;
  }
  const attribution = [img.credit, img.rights].filter(Boolean).join(" · ");
  return (
    <figure className="relative shrink-0 w-[132px] sm:w-[168px] md:w-[200px]"
            data-testid={`record-portrait-${targetId}`}>
      <div className="relative border border-[#3A3630] bg-black p-1.5"
           style={{ boxShadow: "0 8px 24px rgba(0,0,0,0.55)" }}>
        <img src={full(img.urls.webp || img.urls.jpeg || img.urls.thumb_webp)}
             alt={img.alt || name} loading="eager" decoding="async"
             className="w-full h-auto max-h-[260px] object-cover object-top"
             style={{ filter: "sepia(0.18) contrast(1.02)" }} />
        <span aria-hidden="true"
              className="absolute -top-px -left-px w-3 h-3 border-t border-l border-[#D92D20]/70" />
        <span aria-hidden="true"
              className="absolute -bottom-px -right-px w-3 h-3 border-b border-r border-[#D92D20]/70" />
      </div>
      <figcaption className="mt-1.5 flex items-start justify-between gap-2">
        <span className="font-mono text-[8px] uppercase tracking-[0.3em] text-[#757065]">
          {img.verification === "verified" ? "Authenticated" : "On file"}
          {credit && attribution && (
            <span className="block normal-case tracking-normal text-[8px] text-[#5A554C] mt-0.5">
              {attribution}
            </span>
          )}
        </span>
        {attribution && (
          <button type="button" onClick={() => setCredit((v) => !v)}
                  data-testid={`record-portrait-credit-${targetId}`}
                  title="Attribution" aria-label="Toggle attribution"
                  className="shrink-0 text-[#5A554C] hover:text-[#A68F5E] transition-colors">
            <Info size={10} />
          </button>
        )}
      </figcaption>
    </figure>
  );
};

/* Small contained thumbnail for detail panels / graph focus cards.
   Never breaks the layout — falls back to the provided placeholder. */
export const RecordThumb = ({ targetId, targetType = "any", className = "", fallback = null }) => {
  const images = useRecordMedia(targetType, targetId);
  const img = images && images.find((i) => !["internal_only", "background_reference"].includes(i.role));
  if (!img) return fallback;
  return (
    <img src={full(img.urls.thumb_webp)} alt={img.alt} loading="lazy" decoding="async"
         className={className || "max-w-full max-h-full object-contain"}
         style={{ filter: "sepia(0.15)" }}
         data-testid={`record-thumb-${targetId}`} />
  );
};
